/**
 * RecordPhotoUploadAfterSave
 *
 * A record's photo (the ObservationPhoto field) is uploaded through POST {Entity}/{IDRecord}/Photo, which creates the
 * image observation and links it to the record. That route needs a record that exists, so the field only holds the
 * chosen image; this wraps the record's PSRS provider so the held image is uploaded once the save has an ID: after
 * createRecord for a new record (its image is held under ID 0), after updateRecord for an existing one.
 *
 * If the upload fails the record is still saved, and the admin is told to add the photo again. Wrapping twice is a
 * no-op. Kept out of the application class so it can be unit-tested without the browser bundle.
 */

/**
 * @param {Object} pPict - the application's pict
 * @param {Array<string>} pRecordSetNames - recordsets whose entity has a photo field (the recordset is the entity)
 */
function installPhotoUploadAfterSave(pPict, pRecordSetNames)
{
	const tmpMetaController = pPict.services.PictSectionRecordSet;
	if (!tmpMetaController || !tmpMetaController.recordSetProviders) { return; }
	for (const tmpRecordSet of pRecordSetNames)
	{
		const tmpProvider = tmpMetaController.recordSetProviders[tmpRecordSet];
		if (!tmpProvider || tmpProvider._photoUploadAfterSaveWrapped)
		{
			if (!tmpProvider) { pPict.log.warn(`Photo upload wiring: ${ tmpRecordSet } PSRS provider not found — skipping.`); }
			continue;
		}
		tmpProvider._photoUploadAfterSaveWrapped = true;
		const tmpIDField = `ID${ tmpRecordSet }`;

		/**
		 * @param {number} pHeldForIDRecord - the ID the form had when the image was chosen (0 for a new record)
		 * @param {Object} pResult - what the save answered: the record, or { Record }
		 * @param {number} [pKnownIDRecord] - the record's ID when the answer does not carry it
		 */
		const fUploadHeldPhoto = async (pHeldForIDRecord, pResult, pKnownIDRecord) =>
		{
			const tmpPhotoField = pPict.providers.ObservationPhoto;
			const tmpSaved = pResult && (pResult.Record || pResult);
			const tmpIDRecord = (tmpSaved && tmpSaved[tmpIDField]) || pKnownIDRecord;
			if (!tmpPhotoField || (typeof tmpPhotoField.savePendingPhoto !== 'function') || !tmpIDRecord) { return; }
			const tmpIDObservation = await tmpPhotoField.savePendingPhoto(tmpRecordSet, pHeldForIDRecord || 0, tmpIDRecord);
			if (tmpIDObservation && tmpSaved && (typeof tmpSaved === 'object'))
			{
				// the route linked it; keep the answer in step so the form shows (and later saves) the new photo
				tmpSaved.IDObservation = tmpIDObservation;
			}
			else if (tmpIDObservation === 0)
			{
				const tmpModal = pPict.views['Pict-Section-Modal'];
				if (tmpModal && (typeof tmpModal.toast === 'function'))
				{
					tmpModal.toast(`${ tmpRecordSet } saved, but its photo could not be uploaded — open it and add the photo again.`, { type: 'warning', duration: 8000 });
				}
			}
		};

		if (typeof tmpProvider.createRecord === 'function')
		{
			const tmpOriginalCreate = tmpProvider.createRecord.bind(tmpProvider);
			tmpProvider.createRecord = async (pRecord) =>
			{
				const tmpResult = await tmpOriginalCreate(pRecord);
				await fUploadHeldPhoto(0, tmpResult);
				return tmpResult;
			};
		}
		if (typeof tmpProvider.updateRecord === 'function')
		{
			const tmpOriginalUpdate = tmpProvider.updateRecord.bind(tmpProvider);
			tmpProvider.updateRecord = async (pRecord) =>
			{
				const tmpResult = await tmpOriginalUpdate(pRecord);
				const tmpIDRecord = pRecord && pRecord[tmpIDField];
				await fUploadHeldPhoto(tmpIDRecord, tmpResult, tmpIDRecord);
				return tmpResult;
			};
		}
		pPict.log.info(`Photo upload wired to ${ tmpRecordSet } PSRS provider (POST ${ tmpRecordSet }/{ID}/Photo after save).`);
	}
}

module.exports = { installPhotoUploadAfterSave };
